import { Box, Checkbox, createStyles, Flex, Group, Stack } from '@mantine/core';
import type {
	AnalyticsMetricType,
	ImportResult,
	MetricType,
} from '@repo/api-codegen';
import type { EntityType } from '@repo/common/enums/entityType';
import { Banner, Text } from '@repo/foundations';
import { DataTable, type DataTableColumn } from '@repo/mantine-datatable';
import { useCallback, useMemo } from 'react';
import { MonitorIcon } from '../../../components/AddMonitorV2/MonitorIcon/MonitorIcon';
import { useTableStyles } from '../../../components/TableV2/TableV2.styles';
import { metricTypeLabel } from '../../../pages/MonitorPage/utils';
import { entityUrl } from '../../../utils/navigationUtils';

function RenderWithMonitorTypeAndIntegrationLogo({
	metricType,
	label,
	integrationType,
	targetId,
	targetTitle,
	targetType,
	onClick,
}: {
	metricType: string;
	label: string;
	integrationType?: string;
	targetId?: string | null;
	targetTitle?: string | null;
	targetType?: string | null;
	onClick?: () => void;
}) {
	const { classes, theme } = useStyles();

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			if (onClick) {
				e.preventDefault();
				e.stopPropagation();
				onClick();
			}
		},
		[onClick]
	);

	const mt = metricTypeLabel(metricType as MetricType);

	const monitorType = integrationType
		? {
				metric_type: metricType as MetricType,
				integration_type: integrationType as AnalyticsMetricType,
			}
		: { metric_type: metricType as MetricType };

	return (
		<Group noWrap>
			<MonitorIcon type={monitorType} />
			<Flex
				direction="column"
				justify="center"
				my={theme.spacing.sm}
				onClick={handleClick}
				style={{ minWidth: 0 }}
			>
				<Text
					weight="semibold"
					size="sm"
					sx={{
						lineHeight: `${theme.other.space[4]}px`,
					}}
					className={classes.ellipsis}
				>
					{label}
				</Text>
				<Text
					size="xs"
					color="text/secondary/default"
					sx={{
						lineHeight: `${theme.other.space[4]}px`,
					}}
					className={classes.ellipsis}
				>
					{targetId && targetType ? (
						<>
							{mt}{' '}
							<a
								href={entityUrl({
									id: targetId,
									entity_type: targetType as EntityType,
								})}
								style={{ color: 'inherit', textDecoration: 'underline' }}
								onClick={(e) => e.stopPropagation()}
							>
								{targetTitle || 'Target'}
							</a>
						</>
					) : (
						mt
					)}
				</Text>
			</Flex>
		</Group>
	);
}

const useStyles = createStyles((theme) => ({
	container: {
		backgroundColor: theme.other.getColor('surface/primary/default'),
		borderRadius: theme.radius.md,
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
	},
	footer: {
		padding: `${theme.spacing.sm} ${theme.spacing.md}`,
		borderTop: `1px solid ${theme.other.getColor('border/primary/default')}`,
		backgroundColor: theme.other.getColor('surface/secondary/default'),
	},
	ellipsis: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
}));

interface ImportSelectProps {
	monitors: ImportResult[];
	selectedMonitors: string[];
	onSelectionChange: (monitorIds: string[]) => void;
	integrationType?: string;
	importError?: string | null;
}

export function ImportSelect({
	monitors,
	selectedMonitors,
	onSelectionChange,
	integrationType,
	importError,
}: ImportSelectProps) {
	const { classes } = useStyles();
	// eslint-disable-next-line no-custom-hooks-in-presentation-components/no-custom-hooks-in-presentation-components
	const { classes: tableClasses } = useTableStyles({
		hideCheckbox: false,
		withHover: true,
	});

	const allSelected =
		monitors.length > 0 && selectedMonitors.length === monitors.length;
	const someSelected =
		selectedMonitors.length > 0 && selectedMonitors.length < monitors.length;

	const handleSelectAll = useCallback(() => {
		if (allSelected) {
			onSelectionChange([]);
		} else {
			onSelectionChange(monitors.map((m) => m.id));
		}
	}, [allSelected, monitors, onSelectionChange]);

	const handleSelectOne = useCallback(
		(monitorId: string) => {
			if (selectedMonitors.includes(monitorId)) {
				onSelectionChange(selectedMonitors.filter((id) => id !== monitorId));
			} else {
				onSelectionChange([...selectedMonitors, monitorId]);
			}
		},
		[selectedMonitors, onSelectionChange]
	);

	const columns: DataTableColumn<ImportResult>[] = useMemo(
		() => [
			{
				accessor: 'id',
				title: (
					<Checkbox
						checked={allSelected}
						indeterminate={someSelected}
						onChange={handleSelectAll}
						size="sm"
					/>
				),
				render: (record) => (
					<Checkbox
						checked={selectedMonitors.includes(record.id)}
						onChange={() => handleSelectOne(record.id)}
						size="sm"
					/>
				),
				width: 40,
			},
			{
				accessor: 'name',
				title: 'Monitors',
				render: (record) => (
					<RenderWithMonitorTypeAndIntegrationLogo
						metricType={record.metric_type}
						label={record.name}
						integrationType={integrationType}
						targetId={record.target_id}
						targetTitle={record.target_title}
						targetType={record.target_type}
					/>
				),
			},
		],
		[
			allSelected,
			someSelected,
			handleSelectAll,
			selectedMonitors,
			handleSelectOne,
			integrationType,
		]
	);

	return (
		<Stack spacing="md">
			{importError && (
				<Banner tone="critical" title="Import Error" message={importError} />
			)}
			<Box className={classes.container}>
				<DataTable<ImportResult>
					records={monitors}
					columns={columns}
					classNames={tableClasses}
					height={350}
					striped={false}
					highlightOnHover
					withBorder={false}
					idAccessor="id"
				/>
				<Flex className={classes.footer} justify="space-between" align="center">
					<Text size="sm" color="text/secondary/default">
						{monitors.length > 0
							? `1–${monitors.length} of ${monitors.length} monitors`
							: '0 monitors'}
					</Text>
					{selectedMonitors.length > 0 && (
						<Text size="sm" weight="semibold" color="text/primary/default">
							{selectedMonitors.length} selected
						</Text>
					)}
				</Flex>
			</Box>
		</Stack>
	);
}
